import { PlusCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Select,
  Typography,
} from 'antd';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPackages } from 'store/reducers/staticHandbooksReducer';
import { SectionTitle } from 'ui/SectionTitle';
import { formatDate } from 'utils';
import { requiredRule } from 'utils/forms/validationRules';
import { taxStatuses } from 'utils/handbooks/pushManagerHandbooks';

function GeneralForm({ initialValues, onSubmit }) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { packagesData } = useSelector((state) => state.staticHandbooks);
  const defaultActiveTabs = {
    registrationDateType: null,
    taxesDateType: null,
    serviceDateType: null,
  };
  const [activeTabs, setActiveTabs] = useState(defaultActiveTabs);

  useEffect(() => {
    if (packagesData.items.length === 0) {
      dispatch(fetchPackages());
    }
  }, []);

  useEffect(() => {
    if (!initialValues) {
      resetState();
    } else {
      prePopulateForm();
    }
  }, [initialValues]);

  const prePopulateForm = () => {
    const {
      title,
      device_type,
      users_is_active,
      period,
      taxes: { taxes_status, taxes_type_date, taxes_input },
      services: { service_day_type, service_date_type, service_input },
      package_or_services,
    } = initialValues;

    const formValues = {
      title,
      taxes_status,
      taxes_type_date,
      package_or_services,
      service_day_type,
      service_date_type,
    };

    const [firstDevice, secondDevice] = device_type;
    const [firstBehavior, secondBehavior] = users_is_active;
    const { start_date, end_date } = period;

    if (firstDevice && secondDevice) {
      formValues.device_type = 'all';
    } else {
      formValues.device_type = firstDevice;
    }

    if (firstBehavior && secondBehavior) {
      formValues.users_is_active = 'all';
    } else {
      formValues.users_is_active = firstBehavior;
    }

    if (start_date && end_date) {
      formValues.registration_date = 'period';
      formValues.period = [dayjs(start_date), dayjs(end_date)];
      handleActiveTabs('registrationDateType', 'period');
    } else {
      formValues.registration_date = 'all';
      handleActiveTabs('registrationDateType', 'all');
    }

    if (service_date_type) {
      if (service_date_type === 'dynamic') {
        formValues.service_dynamic_input = service_input;
      } else {
        formValues.service_static_input = dayjs(service_input);
      }
    }

    if (taxes_type_date) {
      if (taxes_type_date === 'dynamic') {
        formValues.tax_dynamic_input = taxes_input;
      } else {
        formValues.tax_static_input = dayjs(taxes_input);
      }
    }

    setActiveTabs({
      ...activeTabs,
      taxesDateType: taxes_type_date,
      serviceDateType: service_date_type,
    });

    form.setFieldsValue(formValues);
  };

  const normolizePayload = (values) => {
    const data = { ...values };
    const { device_type, registration_date } = values;
    const isActiveClient = values.users_is_active;

    if (device_type === 'all') {
      data.device_type = ['ios', 'android'];
    } else {
      data.device_type = [data.device_type];
    }

    if (isActiveClient === 'all') {
      data.users_is_active = ['active', 'mobile'];
    } else {
      data.users_is_active = [data.users_is_active];
    }

    if (registration_date === 'period') {
      const [start, end] = values.period;

      data.start_date = formatDate(start, 'YYYY-MM-DD');
      data.end_date = formatDate(end, 'YYYY-MM-DD');
    }

    if (values?.tax_static_input) {
      data.taxes_input = formatDate(values.tax_static_input, 'YYYY-MM');
      delete data.tax_static_input;
    }

    if (values?.tax_dynamic_input) {
      data.taxes_input = values.tax_dynamic_input.map(Number);
      delete data.tax_dynamic_input;
    }

    if (values?.service_static_input) {
      data.service_input = formatDate(
        values.service_static_input,
        'YYYY-MM-DD'
      );
      delete data.service_static_input;
    }

    if (values?.service_dynamic_input) {
      data.service_input = values.service_dynamic_input.map(Number);
      delete data.service_dynamic_input;
    }

    delete data.registration_date;
    delete data.period;

    return data;
  };

  const handleActiveTabs = (key, value = null) => {
    const newActiveTabs = { ...activeTabs };
    newActiveTabs[key] = value;
    setActiveTabs(newActiveTabs);
  };

  const handleSubmit = (values) => {
    const payload = normolizePayload(values);

    onSubmit(payload, resetState);
  };

  const resetState = () => {
    form.resetFields();
    setActiveTabs(defaultActiveTabs);
  };

  const renderDropdown = (menu) => {
    return (
      <>
        <Typography.Link
          onClick={() => {
            form.setFieldsValue({
              package_or_services: packagesData.items.map(({ id }) => id),
            });
          }}
          style={{
            display: 'block',
            padding: '8px',
            width: '100%',
          }}
        >
          <PlusCircleOutlined />
          &nbsp; Выбрать все
        </Typography.Link>
        {menu}
      </>
    );
  };

  return (
    <Form
      form={form}
      layout="horizontal"
      labelAlign="right"
      labelCol={{
        span: LABEL_SPAN,
      }}
      wrapperCol={{ span: CONTENT_SPAN }}
      onFinish={handleSubmit}
    >
      <Form.Item name="title" label="Название сегмента" rules={[requiredRule]}>
        <Input allowClear placeholder="Введите название" />
      </Form.Item>

      <SectionTitle>Пользователь</SectionTitle>

      <Form.Item name="device_type" label="Устройство" rules={[requiredRule]}>
        <Radio.Group>
          <Radio.Button value="all">Все</Radio.Button>
          <Radio.Button value="android">Android</Radio.Button>
          <Radio.Button value="ios">IOS</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="users_is_active"
        label="Пользователь"
        rules={[requiredRule]}
      >
        <Radio.Group>
          <Radio.Button value="all">Все</Radio.Button>
          <Radio.Button value="active">Активный</Radio.Button>
          <Radio.Button value="mobile">Неактивный</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="registration_date"
        label="Дата регистрации"
        rules={[requiredRule]}
      >
        <Radio.Group
          onChange={(e) =>
            handleActiveTabs('registrationDateType', e.target.value)
          }
        >
          <Radio.Button value="all">За всё время</Radio.Button>
          <Radio.Button value="period">Период</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {activeTabs.registrationDateType === 'period' && (
        <Form.Item
          name="period"
          label="Дата начала и окончания"
          rules={[requiredRule]}
        >
          <DatePicker.RangePicker
            format="DD.MM.YYYY"
            allowClear
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}

      <SectionTitle>Налоги</SectionTitle>

      <Form.Item name="taxes_status" label="Cтатус">
        <Select allowClear placeholder="Выберите статус" mode="multiple">
          {taxStatuses.map(({ text, value }) => (
            <Select.Option key={value} value={value}>
              {text}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="taxes_type_date" label="Дата месяца">
        <Radio.Group
          onChange={(e) => handleActiveTabs('taxesDateType', e.target.value)}
        >
          <Radio.Button value="dynamic">Динамичная</Radio.Button>
          <Radio.Button value="static">Статичная</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {activeTabs.taxesDateType === 'dynamic' && (
        <>
          <Form.Item
            name="tax_dynamic_input"
            label="До/после"
            rules={[requiredRule]}
            style={{ marginBottom: 10 }}
          >
            <Select
              allowClear
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Введите до/после"
            />
          </Form.Item>

          <Col offset={CONTENT_SPAN}>
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              -1 если предыдущий месяц,
            </Typography.Paragraph>

            <Typography.Paragraph type="secondary">
              0 если текущий месяц
            </Typography.Paragraph>
          </Col>
        </>
      )}

      {activeTabs.taxesDateType === 'static' && (
        <Form.Item name="tax_static_input" label="Дата" rules={[requiredRule]}>
          <DatePicker
            format="MM.YYYY"
            picker="month"
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}

      <SectionTitle>Услуги</SectionTitle>

      <Form.Item name="package_or_services" label="Пакет или услуг">
        <Select
          allowClear
          placeholder="Выберите услугу"
          mode="multiple"
          dropdownRender={renderDropdown}
        >
          {packagesData.items.map(({ title, id }) => (
            <Select.Option key={id} value={id}>
              {title}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="service_day_type" label="Тип">
        <Radio.Group>
          <Radio.Button value="start">Начало</Radio.Button>
          <Radio.Button value="end">Окончания</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="service_date_type" label="Дата месяца">
        <Radio.Group
          onChange={(e) => handleActiveTabs('serviceDateType', e.target.value)}
        >
          <Radio.Button value="dynamic">Динамичная</Radio.Button>
          <Radio.Button value="static">Статичная</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {activeTabs.serviceDateType === 'dynamic' && (
        <>
          <Form.Item
            name="service_dynamic_input"
            label="До/после"
            rules={[requiredRule]}
            style={{ marginBottom: 10 }}
          >
            <Select
              allowClear
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Введите до/после"
            />
          </Form.Item>

          <Col offset={CONTENT_SPAN}>
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              -3 /если до окончания услуги осталось 3 дня,
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              0 /если тот же день
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary">
              +2 если c начала прошло 2 дня
            </Typography.Paragraph>
          </Col>
        </>
      )}

      {activeTabs.serviceDateType === 'static' && (
        <Form.Item
          name="service_static_input"
          label="Дата"
          rules={[requiredRule]}
        >
          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
        </Form.Item>
      )}

      <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
        <Button type="primary" htmlType="submit">
          Сохранить
        </Button>
      </Form.Item>
    </Form>
  );
}

export { GeneralForm };
